import type { FC } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { ModalContext, RicosContext } from 'ricos-context';
import type { IToolbarItem } from 'ricos-types';
import { DropdownButton } from 'wix-rich-content-toolbars-ui';

import { LayoutGridIcon } from '../assets/icons';
import type { SettingsModalProps } from '../components/settings-modal';
import { SettingsModal } from '../components/settings-modal';
import { POLL_SETTINGS_LAYOUT_MODAL_ID, POLL_SETTINGS_MODAL_ID, SETTINGS_TAB } from '../consts';
import type { PollPluginEditorConfig } from '../types';
import { layoutData } from './PollButtonsData';

type Props = {
  toolbarItem: IToolbarItem;
  id: string;
  dataHook?: string;
  config?: PollPluginEditorConfig;
};

export const PollLayoutButton: FC<Props> = ({ toolbarItem, id, dataHook, config = {} }) => {
  const { t, isMobile } = useContext(RicosContext) || {};
  const modalService = useContext(ModalContext) || {};
  const node = toolbarItem.attributes.selectedNode;
  const getSelectedLayout: () => string = () => node?.attrs?.layout?.poll?.type || 'GRID';
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const closeModal = () => modalService.closeModal(id);
  const selectedLayout = layoutData.find(({ commandKey }) => commandKey === getSelectedLayout());
  const Icon = selectedLayout?.icon || LayoutGridIcon;
  const label = selectedLayout?.text || 'Poll_PollSettings_Tab_Layout_Section_Answers_Layout_Grid';

  const onLayoutClick = layout => {
    toolbarItem.commands?.click({ value: layout });
    closeModal();
  };

  const onCustomizeButtonClick = () => {
    const componentProps: SettingsModalProps = {
      nodeId: toolbarItem.attributes.selectedNode.attrs.id,
      activeTab: SETTINGS_TAB.layout,
      config,
    };

    modalService?.openModal(POLL_SETTINGS_MODAL_ID, {
      componentProps,
      positioning: { placement: 'right' },
      layout: isMobile ? 'fullscreen' : 'drawer',
    });
    closeModal();
  };

  const onClick = () => {
    modalService.isModalOpen(id)
      ? closeModal()
      : modalService?.openModal(id, {
          componentProps: {
            getSelectedLayout,
            onClick: onLayoutClick,
            onCustomizeButtonClick,
            closeModal,
          },
          layout: isMobile ? 'drawer' : 'toolbar',
          positioning: { referenceElement, placement: 'bottom' },
        });
  };

  useEffect(() => {
    !modalService.getModal(POLL_SETTINGS_LAYOUT_MODAL_ID) &&
      modalService.register({
        Component: SettingsModal,
        id: POLL_SETTINGS_LAYOUT_MODAL_ID,
      });
  }, []);

  return (
    <DropdownButton
      dataHook={dataHook}
      active={modalService.isModalOpen(id)}
      onClick={onClick}
      setRef={setReferenceElement}
      Icon={Icon}
      label={t(label)}
      tooltip={t('Poll_PollSettings_Tab_Layout_TabName')}
    />
  );
};
